import { Component, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';

/*
  A collapsible JSON tree for Resource Test. Objects and arrays start closed, apart from the paths
  in `expand`, and any value whose path is in `notes` is highlighted with the note beside it -
  green if its path is also in `good`, red otherwise.
  Paths are keys joined with '/', from the root value, e.g. resources/2/concurrentUses.
*/
@Component({
  selector: 'app-json-node',
  template: `
    <div class="jn-row" [class.jn-hit]="note && !isGood" [class.jn-good]="isGood">
      <span class="jn-toggle" (click)="open = !open">{{isObject ? (open ? '▾' : '▸') : ''}}</span>
      @if (key != null) {
        <span [class.jn-index]="isIndex" [class.jn-key]="!isIndex">{{isIndex ? key + ':' : '"' + key + '":'}}</span>
      }
      @if (isObject) {
        <span>{{isArray ? '[' : '{'}}</span>
        @if (!open) {
          <span class="jn-summary" (click)="open = true">{{summary}}</span>
        }
        @if (!open) {
          <span>{{isArray ? ']' : '}'}}</span>
        }
      }
      @if (!isObject) {
        <span [ngClass]="leafClass">{{leafText}}</span>
      }
      @if (note) {
        <span class="jn-note">← {{note}}</span>
      }
    </div>
    @if (isObject && open) {
      <div class="jn-children">
        @for (e of entries; track trackKey($index, e)) {
          <app-json-node [key]="e.key" [isIndex]="isArray" [value]="e.value" [path]="e.path" [notes]="notes" [good]="good" [expand]="expand"></app-json-node>
        }
      </div>
    }
    @if (isObject && open) {
      <div class="jn-row"><span class="jn-toggle"></span>{{isArray ? ']' : '}'}}</div>
    }
    `,
  styleUrls: ['./jsonnode.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class JsonNodeComponent implements OnChanges {
  @Input() key: string | null = null;
  @Input() isIndex: boolean = false;
  @Input() value: any;
  @Input() path: string = '';
  @Input() notes: Map<string, string> = new Map();
  @Input() good: Set<string> = new Set();
  @Input() expand: Set<string> = new Set();

  open: boolean = false;
  isObject: boolean = false;
  isArray: boolean = false;
  entries: { key: string, value: any, path: string }[] = [];
  summary: string = '';
  leafText: string = '';
  leafClass: string = '';
  note: string | undefined;
  isGood: boolean = false;

  ngOnChanges(): void {
    this.isObject = this.value !== null && typeof this.value === 'object';
    this.isArray = Array.isArray(this.value);
    this.note = this.notes?.get(this.path);
    this.isGood = this.good?.has(this.path) ?? false;

    //The root is always open
    this.open = this.path === '' || (this.expand?.has(this.path) ?? false);

    if (this.isObject) {
      const keys = this.isArray ? this.value.map((_, i) => String(i)) : Object.keys(this.value);
      this.entries = keys.map(k => ({ key: k, value: this.value[k], path: this.path === '' ? k : this.path + '/' + k }));

      if (this.isArray)
        this.summary = keys.length + (keys.length == 1 ? ' item' : ' items');
      else
        this.summary = this.value.name != null ? 'name: "' + this.value.name + '" …' : keys.length + ' keys';
    } else {
      this.leafText = typeof this.value === 'string' ? '"' + this.value + '"' : String(this.value);
      this.leafClass = typeof this.value === 'string' ? 'jn-string' : typeof this.value === 'number' ? 'jn-number' : 'jn-other';
    }
  }

  trackKey(index: number, e: { key: string }) {
    return e.key;
  }
}
